import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";

function EligibilityPanUpload() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  const captureRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState<{ panId?: string; panImage?: string } | null>(null);

  const startOcr = async (file: File) => {
    setOcrLoading(true);
    setOcrResult(null);
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const formData = new FormData();
      formData.append("aadhaarFront", "");
      formData.append("aadhaarBack", "");
      formData.append("pan", file);
      formData.append("others", "");
      const res = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/ocr/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("OCR start failed");
      const json = await res.json();
      const ocrId = json?.data?.id;
      if (!ocrId) throw new Error("No OCR ID returned");

      // Poll for result
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const pollRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/ocr/data?id=${ocrId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const pollJson = await pollRes.json();
          const d = pollJson?.data;
          if (d?.panId) {
            setOcrResult({ panId: d.panId, panImage: d.panImage || undefined });
            setOcrLoading(false);
            return;
          }
        } catch {}
      }
      // Polling exhausted — no panId, but still allow upload via file/upload fallback
    } catch {}
    setOcrLoading(false);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed (JPG, PNG, etc.).");
      return;
    }
    setError("");
    setImage(file);
    setPreview(URL.createObjectURL(file));
    startOcr(file);
  };

  const handleRemove = () => {
    if (preview) URL.revokeObjectURL(preview);
    setImage(null);
    setPreview(null);
    setError("");
    setOcrResult(null);
    setOcrLoading(false);
  };

  const handleUpload = async () => {
    if (!image || uploading) return;
    setUploading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const userId = (userData as any)?.userId;

      // Get s3Url: use panImage from OCR if available, otherwise call file/upload
      let s3Url = ocrResult?.panImage || null;
      if (!s3Url) {
        const fd = new FormData();
        fd.append("file", image);
        fd.append("type", "pan");
        fd.append("base64", "");
        const uploadRes = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/file/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!uploadRes.ok) throw new Error("Upload failed");
        const uploadJson = await uploadRes.json();
        s3Url = uploadJson?.data?.s3Url;
      }

      // Create document record
      await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/users/documents/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          type: "pan",
          url1: s3Url,
          documentId: ocrResult?.panId || "",
        }),
      });

      navigate("/eligibility/waiting", { replace: true, state: { data: userData, fromStage10: true } });
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div style={{
                  width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                  background: "#F4E5DD", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="5" width="20" height="14" rx="2" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" />
                    <circle cx="8" cy="11" r="2" stroke="#d32028" strokeWidth="1.4" />
                    <path d="M13 9H18M13 13H16" stroke="#d32028" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>PAN Card Upload</p>
              </div>

              <p style={{ fontSize: "0.8rem", color: "#888", margin: "0 0 1.1rem", lineHeight: 1.6 }}>
                Please upload a clear photo of your PAN card. Make sure all details are visible and the image is not blurry.
              </p>

              {!preview ? (
                <>
                  {/* ── Option cards ── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>

                    {/* Capture — mobile only */}
                    {isMobile && (
                    <button
                      onClick={() => captureRef.current?.click()}
                      style={{
                        display: "flex", alignItems: "center", gap: "1rem",
                        background: "#f0fdf4", border: "1.5px dashed #22c55e",
                        borderRadius: "14px", padding: "1rem 1.2rem",
                        cursor: "pointer", textAlign: "left", width: "100%",margin:"-0.1px"
                      }}
                    >
                      <div style={{
                        width: "44px", height: "44px", borderRadius: "12px",
                        background: "#dcfce7", display: "flex", alignItems: "center",
                        justifyContent: "center", flexShrink: 0,
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="13" r="4" stroke="#16a34a" strokeWidth="1.6" />
                        </svg>
                      </div>
                      <div>
                        <p style={{ margin: "0 0 0.15rem", fontSize: "0.9rem", fontWeight: 700, color: "#1a1a2e", fontFamily: "Outfit, sans-serif" }}>
                          Take a Photo
                        </p>
                        <p style={{ margin: 0, fontSize: "0.74rem", color: "#888" }}>
                          Use your camera to capture PAN card
                        </p>
                      </div>
                    </button>
                    )}

                    {/* Upload */}
                    <button
                      onClick={() => uploadRef.current?.click()}
                      style={{
                        display: "flex", alignItems: "center", gap: "1rem",
                        background: "#f0fdf4", border: "1.5px dashed #22c55e",
                        borderRadius: "14px", padding: "1rem 1.2rem",
                        cursor: "pointer", textAlign: "left", width: "100%",margin:"-0.1px"
                      }}
                    >
                      <div style={{
                        width: "44px", height: "44px", borderRadius: "12px",
                        background: "#dcfce7", display: "flex", alignItems: "center",
                        justifyContent: "center", flexShrink: 0,
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <polyline points="17 8 12 3 7 8" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <line x1="12" y1="3" x2="12" y2="15" stroke="#16a34a" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <p style={{ margin: "0 0 0.15rem", fontSize: "0.9rem", fontWeight: 700, color: "#1a1a2e", fontFamily: "Outfit, sans-serif" }}>
                          {isMobile ? "Upload from Gallery" : "Upload Image"}
                        </p>
                        <p style={{ margin: 0, fontSize: "0.74rem", color: "#888" }}>
                          {isMobile ? "Select an existing image from your device" : "Select a photo of your PAN card from your device"}
                        </p>
                      </div>
                    </button>
                  </div>

                  {/* ── Tips ── */}
                  <div style={{ marginTop: "1rem", background: "#f9f9f9", borderRadius: "10px", padding: "0.8rem 1rem" }}>
                    <p style={{ fontSize: "0.72rem", fontWeight: 700, color: "#555", margin: "0 0 0.4rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Tips for a good photo</p>
                    {["Ensure all four corners of the PAN card are visible", "Avoid glare or shadows on the card", "Hold the camera steady for a clear image"].map((tip) => (
                      <div key={tip} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.3rem" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: "2px" }}>
                          <path d="M9 12L11 14L15 10" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="9" stroke="#22c55e" strokeWidth="1.5" />
                        </svg>
                        <p style={{ margin: 0, fontSize: "0.73rem", color: "#777", lineHeight: 1.5 }}>{tip}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* ── Preview ── */
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 0.6rem" }}>
                    <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "#555", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>Preview</p>
                    <button
                      onClick={handleRemove}
                      style={{
                        width: "26px", height: "26px", borderRadius: "50%",
                        background: "#f0f0f0", border: "none",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="#555" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                  <div style={{
                    borderRadius: "12px", overflow: "hidden",
                    border: error
                      ? "2px solid #d32028"
                      : ocrResult?.panId && !ocrLoading
                        ? "2px solid #22c55e"
                        : "1px solid #e8e8e8",
                    boxShadow: !error && ocrResult?.panId && !ocrLoading ? "0 0 0 4px rgba(34, 197, 94, 0.12)" : "none",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                  }}>
                    <img
                      src={preview}
                      alt="PAN card preview"
                      style={{ width: "100%", display: "block", maxHeight: "220px", objectFit: "cover" }}
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", color: "#888", margin: "0.5rem 0 0" }}>
                    {image?.name} &nbsp;·&nbsp; {image ? (image.size / 1024).toFixed(0) : 0} KB
                  </p>
                  {ocrLoading && (
                    <p style={{ fontSize: "0.75rem", color: "#888", margin: "0.4rem 0 0" }}>Reading PAN details…</p>
                  )}
                  {ocrResult?.panId && !ocrLoading && (
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: "0.35rem",
                      margin: "0.5rem 0 0", padding: "0.25rem 0.7rem",
                      background: "#dcfce7", color: "#15803d", borderRadius: "999px",
                      fontSize: "0.75rem", fontWeight: 600,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M5 12.5L10 17.5L19 7" stroke="#15803d" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      PAN detected: {ocrResult.panId}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.82rem", margin: "0.75rem 0 0" }}>{error}</p>
              )}
            </div>

            {/* ── Buttons ── */}
            <div style={{ padding: "0.75rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "0.6rem" }}>
              <button
                onClick={handleUpload}
                disabled={!image || uploading}
                style={{
                  width: "55%",
                  padding: "0.85rem 1.5rem",
                  background: !image || uploading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: !image || uploading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem"
                }}
              >
                {uploading ? "Uploading..." : (
                  <>
                    Upload
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/course-details", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline"}}
                >
                  Skip
                </button>
              )}
            </div>
            <Footer light />
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={captureRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <input
          ref={uploadRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </div>
    </div>
  );
}

export default EligibilityPanUpload;
